import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Stat, Loading, CHART_GRID } from "@/components/kit";

const METRICS = [
  { key: "transactions", label: "Orders" },
  { key: "revenue", label: "Revenue" },
  { key: "coffee", label: "Coffee" },
  { key: "food", label: "Food" },
];

export default function Accuracy() {
  const { activeStore } = useAuth();
  const [metric, setMetric] = useState("transactions");
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!activeStore) return;
    setD(null);
    api.get("/accuracy", { params: { store_id: activeStore, metric, window: 30 } }).then(({ data }) => setD(data));
  }, [activeStore, metric]);

  return (
    <div>
      <PageHeader
        title="Forecast Accuracy"
        subtitle="Predicted vs actual sales — tracked daily"
        right={
          <div className="flex gap-1 bg-zinc-100 rounded-lg p-1">
            {METRICS.map((m) => (
              <button key={m.key} data-testid={`metric-${m.key}`} onClick={() => setMetric(m.key)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors duration-200 ${metric === m.key ? "bg-white shadow-sm text-zinc-900 font-medium" : "text-zinc-500"}`}>
                {m.label}
              </button>
            ))}
          </div>
        }
      />

      {!d ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat testid="mape-7d" label="7-day MAPE" value={`${d.metrics_7d.mape}%`} accent="text-emerald-600" />
            <Stat testid="mape-30d" label="30-day MAPE" value={`${d.metrics.mape}%`} />
            <Stat label="MAE" value={num(d.metrics.mae, 1)} sub="mean abs. error" />
            <Stat label="RMSE / Bias" value={`${num(d.metrics.rmse, 1)} / ${d.metrics.bias >= 0 ? "+" : ""}${num(d.metrics.bias, 1)}`} />
          </div>

          <Card className="p-6 mt-6" testid="accuracy-chart">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-4">Predicted vs actual · {METRICS.find((m) => m.key === metric).label}</div>
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={d.series} margin={{ left: 10 }}>
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="predicted" stroke="#18181b" strokeWidth={2} dot={false} name="Predicted" />
                <Line type="monotone" dataKey="actual" stroke="#f59e0b" strokeWidth={2} dot={false} name="Actual" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}
    </div>
  );
}
